import config from 'shared/config'
import { transform as transformErrorReporter } from 'shared/errorReporter'
import { type User } from './context/AuthContext'
import { type Client } from './context/ClientContext'
import logger from 'shared/logger'

const log = logger().child({
  namespace: 'logRocket'
})
const NODE_ENV = config('APP_ENV') as string

const LOGROCKET_APP_ID = config('NEXT_PUBLIC_LOGROCKET_APP_ID') as string

const canUseLogRocket = NODE_ENV === 'production' && LOGROCKET_APP_ID != null

/** logrocket uses `export =`; interop may expose `.default` at runtime. */
interface LogRocketApi {
  init: (
    appId: string,
    options?: {
      network?: {
        requestSanitizer?: (request: {
          headers: Record<string, string | null | undefined>
          body?: string | null
          url?: string
        }) => unknown
      }
    }
  ) => void
  identify: (userId: string, traits?: Record<string, string | number | boolean>) => void
  getSessionURL: (callback: (sessionURL: string) => void) => void
}

let logRocketPromise: Promise<LogRocketApi> | null = null

const CREDENTIAL_KEYS_TO_REDACT = [
  'accessToken',
  'access_token',
  'refreshToken',
  'refresh_token',
  'externalUserId',
  'external_user_id'
] as const

const sanitizeCredentialsBody = (body: string): string | null => {
  try {
    const parsed = JSON.parse(body) as Record<string, unknown>
    for (const key of CREDENTIAL_KEYS_TO_REDACT) {
      if (key in parsed) parsed[key] = '[REDACTED]'
    }
    return JSON.stringify(parsed)
  } catch {
    return null
  }
}

const resolveLogRocket = (mod: LogRocketApi | { default: LogRocketApi }): LogRocketApi => {
  if (typeof mod === 'object' && mod !== null && 'default' in mod && mod.default != null) {
    return mod.default
  }
  return mod as LogRocketApi
}

const loadLogRocket = async (): Promise<LogRocketApi | null> => {
  if (!canUseLogRocket || typeof window === 'undefined') {
    return null
  }
  logRocketPromise ??= import('logrocket').then((mod) =>
    resolveLogRocket(mod as LogRocketApi | { default: LogRocketApi })
  )
  return await logRocketPromise
}

function setUp(): void {
  void (async () => {
    try {
      const LogRocket = await loadLogRocket()
      if (LogRocket == null) {
        return
      }

      LogRocket.init(LOGROCKET_APP_ID, {
        network: {
          requestSanitizer: (request) => {
            // eslint-disable-next-line @typescript-eslint/dot-notation
            request.headers['Authorization'] = ''

            if (request.url?.includes('/credentials') === true && request.body != null && request.body !== '') {
              request.body = sanitizeCredentialsBody(request.body)
            }

            return request
          }
        }
      })

      LogRocket.getSessionURL((sessionURL) => {
        transformErrorReporter({ sessionUrl: sessionURL })
      })
    } catch (e) {
      log.error(`Error setting up LogRocket: ${(e as Error).message}`)
    }
  })()
}

export const identify = ({ user, client }: { user: User; client: Client }): void => {
  void (async () => {
    try {
      if (user.id == null) {
        return
      }
      const LogRocket = await loadLogRocket()
      if (LogRocket == null) {
        return
      }

      LogRocket.identify(user.id.toString(), {
        name: user.name,
        email: user.email,
        userId: user.id.toString(),
        client: client.name,
        clientId: client.id,
        workspace: client.name
      })
    } catch (e) {
      log.error(`Error identifying user: ${(e as Error).message}`)
    }
  })()
}

export default setUp
